import type { Key, ReactNode, Ref } from "react";
import classNames from "classnames";
import type { TooltipBodyProps } from "@design-system/Tooltip/Tooltip.types";
import {
    AvatarGroupSizeContext,
    type AvatarGroupSize,
} from "./avatarGroupContext";
import { AvatarOverflowChip } from "./AvatarOverflowChip";
import { getAvatarVisibleCount } from "./avatarVisibleCount";
import styles from "./AvatarGroup.module.scss";

// Visual source of truth: docs/specs/2026-09-28-avatar-figma-rework.md and
// docs/specs/2026-09-29-entity-class-workspace-avatars.md.

export type { AvatarGroupSize } from "./avatarGroupContext";

/**
 * Which avatar is drawn whole where two overlap: `lastOnTop` masks each avatar
 * by the next one's shape; `firstOnTop` masks each by the previous one's
 * circle, so the first avatar (say, an agent) reads as the lead.
 */
export type AvatarGroupStacking = "lastOnTop" | "firstOnTop";

export interface AvatarGroupProps<T> {
    items: readonly T[];
    getKey: (item: T) => Key;
    /** Name of an item behind +N, read out and listed in the chip's tooltip. */
    getLabel: (item: T) => string;
    renderAvatar: (item: T) => ReactNode;
    /** Visible avatars before the +N chip; one extra item shows as an avatar instead of "+1", unless `extraCount` adds a chip anyway. @default 4 */
    max?: number;
    /** Items the group does not list, added to the +N chip. @default 0 */
    extraCount?: number;
    /** Size of every avatar without an explicit `size`, and of the chip. @default "xs" */
    size?: AvatarGroupSize;
    /** @default "lastOnTop" */
    stacking?: AvatarGroupStacking;
    /** The +N chip's tooltip for the hidden items; defaults to "N more" and their labels. */
    getOverflowTooltip?: (hiddenItems: readonly T[]) => TooltipBodyProps;
    className?: string;
    ref?: Ref<HTMLSpanElement>;
}

/**
 * A stack of avatars with a +N chip for the items past `max` plus
 * `extraCount`. Avatars keep their full size and overlap behind a transparent
 * seam shaped like the avatar on top (circle, square corners or diamond point).
 * Each visible avatar keeps its own accessible name; the chip lists the hidden
 * items in a tooltip on hover (or `getOverflowTooltip`'s body), is hidden from
 * assistive tech and replaced by visually hidden "and N more: ..." text.
 * Marked `data-avatar-box` like `Avatar`, so a Tag holding it pads to fit.
 */
export const AvatarGroup = <T,>({
    items,
    getKey,
    getLabel,
    renderAvatar,
    max = 4,
    extraCount = 0,
    size = "xs",
    stacking = "lastOnTop",
    getOverflowTooltip,
    className,
    ref,
}: Readonly<AvatarGroupProps<T>>) => {
    const visibleCount = getAvatarVisibleCount({
        itemCount: items.length,
        max,
        extraCount,
    });
    const hiddenItems = items.slice(visibleCount);

    return (
        <AvatarGroupSizeContext.Provider value={size}>
            <span
                ref={ref}
                role="group"
                data-avatar-box=""
                data-size={size}
                data-stacking={stacking}
                className={classNames(styles.group, className)}
            >
                <span className={styles.stack}>
                    {items.slice(0, visibleCount).map((item) => (
                        <span key={getKey(item)} className={styles.item}>
                            {renderAvatar(item)}
                        </span>
                    ))}
                </span>
                <AvatarOverflowChip
                    hiddenLabels={hiddenItems.map(getLabel)}
                    extraCount={extraCount}
                    size={size}
                    tooltip={getOverflowTooltip?.(hiddenItems)}
                />
            </span>
        </AvatarGroupSizeContext.Provider>
    );
};
